import { z } from 'zod';

/**
 * A real calendar date in YYYY-MM-DD. The regex alone admits impossible dates
 * (2026-02-30), so round-trip through Date.UTC to reject them.
 */
function isRealIsoDate(s: string): boolean {
  const parts = s.split('-').map(Number);
  const y = parts[0] ?? NaN;
  const m = parts[1] ?? NaN;
  const d = parts[2] ?? NaN;
  const date = new Date(Date.UTC(y, m - 1, d));
  return (
    date.getUTCFullYear() === y &&
    date.getUTCMonth() === m - 1 &&
    date.getUTCDate() === d
  );
}

export const FechaTrabajoSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Fecha invalida (formato YYYY-MM-DD)')
  .refine(isRealIsoDate, { message: 'Fecha invalida (dia inexistente)' })
  // Year bounds: a native date input being typed digit-by-digit fires change
  // events with years like 0002 — a real calendar date the regex+roundtrip
  // accept. Work dates outside this window are always input mistakes.
  .refine((s) => s >= '2000-01-01' && s <= '2100-12-31', {
    message: 'Fecha fuera de rango (2000-2100)',
  });

/**
 * Work dates a crew member is assigned to (legacy "+ Fecha" parity).
 * Bounded to 62 entries: a ticket never spans more than ~2 months of work days.
 */
export const UpdateCuadrillaFechasSchema = z.object({
  fechas: z.array(FechaTrabajoSchema).min(1).max(62),
});

export const AssignWorkerSchema = z.object({
  usuarioId: z.number().int().positive(),
  rol: z.string().max(50).optional(),
  cargo: z.string().max(100).optional(),
  cargoId: z.number().int().positive().optional(),
  // Optional explicit seed; when omitted the API seeds from ot.fechaProgramada
  // (legacy FECHAPROGCUADRILLA=SI behavior). min(1) so an explicit [] can't
  // silently defeat the seed — omit the field to get the default.
  fechas: z.array(FechaTrabajoSchema).min(1).max(62).optional(),
});

export const CargoUsuarioSchema = z.object({
  id: z.number().int().positive(),
  nombre: z.string().min(1).max(100),
  codigo: z.string().min(1).max(20),
  activo: z.boolean().default(true),
  orden: z.number().int().default(0),
});

export type CargoUsuarioInput = z.infer<typeof CargoUsuarioSchema>;

export const WorkerSearchSchema = z.object({
  q: z.string().min(1).max(100).optional(),
  role: z.coerce.number().int().positive().optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});

/**
 * Validate Chilean RUT format: XX.XXX.XXX-X or XXXXXXXX-X
 * Also validates the check digit (digito verificador).
 */
function isValidRut(rut: string): boolean {
  // Remove dots and dashes, uppercase
  const clean = rut.replace(/\./g, '').replace(/-/g, '').toUpperCase();
  if (clean.length < 2) return false;

  const body = clean.slice(0, -1);
  const dv = clean.slice(-1);

  // Body must be numeric
  if (!/^\d+$/.test(body)) return false;

  // Calculate expected DV
  let sum = 0;
  let multiplier = 2;
  for (let i = body.length - 1; i >= 0; i--) {
    sum += parseInt(body.charAt(i), 10) * multiplier;
    multiplier = multiplier === 7 ? 2 : multiplier + 1;
  }
  const remainder = 11 - (sum % 11);
  const expectedDv = remainder === 11 ? '0' : remainder === 10 ? 'K' : String(remainder);

  return dv === expectedDv;
}

export const CreateWorkerSchema = z.object({
  rut: z.string().min(3).max(20).refine(isValidRut, { message: 'RUT invalido (digito verificador incorrecto)' }),
  nombre: z.string().min(1).max(200),
  apaterno: z.string().min(1).max(100),
  amaterno: z.string().max(100).optional(),
  email: z.string().email().max(200).optional(),
  celular: z.string().max(20).optional(),
  cargo: z.string().max(100).optional(),
  especialidad: z.string().max(100).optional(),
  empresaId: z.number().int().positive().optional(),
  activo: z.boolean().default(true),
});

export const UpdateWorkerSchema = z.object({
  rut: z.string().min(3).max(20).refine(isValidRut, { message: 'RUT invalido (digito verificador incorrecto)' }).optional(),
  nombre: z.string().min(1).max(200).optional(),
  apaterno: z.string().min(1).max(100).optional(),
  amaterno: z.string().max(100).optional(),
  email: z.string().email().max(200).optional(),
  celular: z.string().max(20).optional(),
  cargo: z.string().max(100).optional(),
  especialidad: z.string().max(100).optional(),
  empresaId: z.number().int().positive().optional(),
  activo: z.boolean().optional(),
});

export type CreateWorkerInput = z.infer<typeof CreateWorkerSchema>;
export type UpdateWorkerInput = z.infer<typeof UpdateWorkerSchema>;
export type AssignWorkerInput = z.infer<typeof AssignWorkerSchema>;
export type UpdateCuadrillaFechasInput = z.infer<typeof UpdateCuadrillaFechasSchema>;
export type WorkerSearch = z.infer<typeof WorkerSearchSchema>;
